import React, {useEffect, useState} from 'react';
import * as _ from 'lodash';
import axios from 'axios';
import {Uppy} from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import {DashboardModal} from "@uppy/react";
import * as moment from 'moment';
import * as UUIDv4 from 'uuid/v4';

import './VaultForm.scss';
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Tooltip from "@material-ui/core/Tooltip";
import Slider from "@material-ui/core/Slider";
import {humanFileSize} from "../../utils/helpers";
import PersonInfoModal from "./PersonInfoModal";
import {BASE_ROUTE} from "../../utils/api-client";
import {accessDelayMapping, GUARDIAN, KEYHOLDER} from "../../config/constants";
import {compose} from "redux";
import {connect} from "react-redux";
import {openConfirmationModal} from "../../redux/reducers/utils";
import FdlOutlinedInput from "../../utils/FdlOutlinedInput/FdlOutlinedInput";
import Divider from "@material-ui/core/Divider";
import {navigate} from "@reach/router";
import ClientDocumentsModal from "./ClientDocumentsModal";


function VaultForm(props) {
  // vault attributes
  let [vaultId, setVaultId] = useState(props.vault ? props.vault.id : UUIDv4());
  let [vaultName, setVaultName] = useState(props.vault ? props.vault.vault_name : "");
  let [description, setDescription] = useState(props.vault ? props.vault.description : "");
  let [vaultPurpose, setVaultPurpose] = useState(props.vault ? props.vault.purpose : "");
  let [keyholders, setKeyholders] = useState(props.vault ? props.vault.keyholders : []);
  let [guardians, setGuardians] = useState(props.vault ? props.vault.guardians : []);
  let [documents, setDocuments] = useState(props.vault ? props.vault.documents : []);
  let [approvalLevel, setApprovalLevel] = useState(props.vault ? props.vault.approval_level : "ALL");
  let [numberApprovals, setNumberApprovals] = useState(props.vault ? props.vault.number_approvals : 1);
  let [accessDelay, setAccessDelay] = useState(props.vault ? props.vault.access_delay : "NONE");

  // form management
  let [allowedStep, setAllowedStep] = useState(props.allowedStep ? props.allowedStep : 0);
  let [activeStep, setActiveStep] = useState(0);
  let [openFileUploadModal, setOpenFileUploadModal] = useState(false);
  let [openNewKeyholderModal, setOpenNewKeyholderModal] = useState(false);
  let [openEditKeyholderModal, setOpenEditKeyholderModal] = useState(false);
  let [openNewGuardianModal, setOpenNewGuardianModal] = useState(false);
  let [openEditGuardianModal, setOpenEditGuardianModal] = useState(false);
  let [openClientDocumentsModal, setOpenClientDocumentsModal] = useState(false);
  let [editedKeyholder, setEditedKeyholder] = useState(null);
  let [editedGuardian, setEditedGuardian] = useState(null);
  let [tempDocuments, setTempDocuments] = useState([]);

  let [uppy, setUppy] = useState( new Uppy({
    autoProceed: true,
    restrictions: {
      maxFileSize: 10**8, // 100MB restriction
      maxNumberOfFiles: 30,
      minNumberOfFiles: 1,
      allowedFileTypes: false
    }}).use(AwsS3, {
    getUploadParameters(file) {
      const data = {
        "file_name": file.name,
        "file_type": file.type,
        "temp_vault": !!!props.vault,
        "vault_id": vaultId
      };
      // if no vault exists, store the file in a temp dir w/ UUID
      return axios.post(BASE_ROUTE + "generate_presigned_url", data).then((response) => {
        return {
          method: "put",
          url: response.data
        }
      })
    }
  }));

  useEffect(() => {
    uppy.on('complete', (result) => {
      const newDocuments = _.map(result.successful, file => {
        return {
          name: file.name,
          type: file.type,
          size: file.size,
          updated: (new Date()).toUTCString()
        }});
      setTempDocuments(newDocuments);
    });
  }, []);

  useEffect(() => {
    if (activeStep > allowedStep) {
      // let user navigate by clicking to previous steps & new current step
      setAllowedStep(activeStep )
    }
  }, [activeStep]);


  useEffect(() => {
    const updatedDocuments = [...documents];
    _.forEach(tempDocuments, (tempDoc) => {
      const index = _.findIndex(updatedDocuments, x => x.name === tempDoc.name);
      if (index === -1) {
        updatedDocuments.push(tempDoc);
      } else {
        updatedDocuments[index] = tempDoc;
      }
    });

    // Note: fixes a very strange bug where 'documents' never gets updated in the uppy.on complete call.
    setDocuments(updatedDocuments);
    setOpenFileUploadModal(false);
  }, [tempDocuments]);

  const steps = ["Basic Details", "Keyholders", "Guardians", "Documents", "Vault Options"];

  const toggleModal = (modalType) => {
    switch(modalType) {
      case "FILE_UPLOADER":
        if (!openFileUploadModal) {
          uppy.reset();
        }

        setOpenFileUploadModal(!openFileUploadModal);
        break;
      case "ADD_KEYHOLDER":
        setOpenNewKeyholderModal(!openNewKeyholderModal);
        break;
      case "EDIT_KEYHOLDER":
        setOpenEditKeyholderModal(!openEditKeyholderModal);
        break;
      case "ADD_GUARDIAN":
        setOpenNewGuardianModal(!openNewGuardianModal);
        break;
      case "EDIT_GUARDIAN":
        setOpenEditGuardianModal(!openEditGuardianModal);
        break;
      case "CLIENT_DOCUMENTS":
        setOpenClientDocumentsModal(!openClientDocumentsModal);
        break;
    }
  };

  const updateKeyholders = (name, email) => {
    if (editedKeyholder !== null) {
      const newKeyholders = [...keyholders];
      newKeyholders[editedKeyholder] = {
        name: name,
        email: email
      };
      setKeyholders(newKeyholders);
      setEditedKeyholder(null);
      
    } else {
      setKeyholders([...keyholders].concat([{email: email, name: name}]));
    }
  };

  const updateGuardians = (name, email) => {
    if (editedGuardian !== null) {
      const newGuardians = [...guardians];
      newGuardians[editedGuardian] = {
        name: name,
        email: email
      };
      setGuardians(newGuardians);
      setEditedGuardian(null);
    } else {
      setGuardians([...guardians].concat([{email: email, name: name}]));
    }
  };

  const editKeyholder = (index) => {
    setEditedKeyholder(index);
    toggleModal("EDIT_KEYHOLDER");
  };

  const removeElement = (index, arr) => {
    const newArr = [...arr];
    newArr.splice(index, 1);
    return newArr;
  };

  const editGuardian = (index) => {
    setEditedGuardian(index);
    toggleModal("EDIT_GUARDIAN");
  };

  const checkStepDisabled = (index) => {
    switch(index) {
      case 0:
        return !(vaultName && vaultPurpose);
      case 1:
      case 2:
      case 3:
        return false;
    }
  };

  const updateDocuments = (clientDocuments) => {
    let newDocuments = [...documents];
    _.forEach(clientDocuments, doc => newDocuments.push(doc));
    setDocuments(newDocuments)
  }

  const saveVault = () => {
    const vault = {
      vault_name: vaultName,
      description: description,
      purpose: vaultPurpose,
      keyholders: keyholders,
      guardians: guardians,
      documents: documents,
      approval_level: approvalLevel,
      number_approvals: numberApprovals,
      access_delay: accessDelay,
      created_by_advisor: false,
    };

    if (props.client) {
      vault.created_by_advisor = true;
      vault.client_name = props.client.name;
      vault.client_email = props.client.email;
      vault.client_id = props.client.id;
    }

    if (!props.vault) {
      vault['temp_id'] = vaultId
    }

    props.saveVault(vault);
  }

  const nextStep = () => {
    switch (activeStep) {
      case 0:
        setActiveStep(activeStep + 1)
        break;
      case 1:
        // keyholders
        if (!keyholders.length) {
          props.openConfirmationModal('NO_KEYHOLDERS', () => setActiveStep(activeStep + 1))
        } else {
          setActiveStep(activeStep + 1)
        }
        break;
      case 2:
        // guardians
        if (!guardians.length) {
          props.openConfirmationModal('NO_GUARDIANS', () => setActiveStep(activeStep + 1))
        } else {
          setActiveStep(activeStep + 1)
        }
        break;
      case 3:
        // documents
        if (!documents.length) {
          props.openConfirmationModal('NO_DOCUMENTS', () => setActiveStep(activeStep + 1))
        } else {
          setActiveStep(activeStep + 1)
        }
        break;
      case 4:
        // options
        saveVault()
    }
  };

  const getStepContent = (index) => {
    switch(index) {
      case 0:
        // Name, Description, Type, Security Level (explanation below w/ hidebox for details)
        return (
          <div>
            <p className="mb-8 text-sm sm:text-base">First, fill out some basic details about your vault.</p>
            <div className="vault-detail-field">
              <FdlOutlinedInput value={vaultName}
                                label={"Name your vault"}
                                hideTopMargin
                                autoFocus
                                onChange={(value)=> setVaultName(value)} />
            </div>
            <div className="mt-8 mb-10">
              <FormControl className="vault-detail-field"
                           margin="dense"
                           variant="outlined">
                <InputLabel className="form-label-override"
                            required
                            shrink={true}>
                 Give it a purpose (choose one)
                </InputLabel>
                <Select value={vaultPurpose} onChange={(event)=> setVaultPurpose(event.target.value)}>
                  <MenuItem value={"ESTATE_PLANNING"}>Estate planning & asset management</MenuItem>
                  <MenuItem value={"EMERGENCY"}>Medical emergency preparation</MenuItem>
                  <MenuItem value={"MISSING"}>"If I go missing" folder</MenuItem>
                  <MenuItem value={"REAL_ESTATE"}>Real estate documents</MenuItem>
                  <MenuItem value={"STORAGE"}>Long-term document storage</MenuItem>
                  <MenuItem value={"OTHER"}>Other</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="vault-detail-field">
              <FdlOutlinedInput value={description}
                                label={"Briefly describe your vault"}
                                hideTopMargin
                                rows={4}
                                onChange={(value)=> setDescription(value)} />
            </div>
          </div>
        );
      case 1:
        // Keyholders
        return (
          <div>
            <p className="text-sm sm:text-base">Keyholders can request access to the documents in your vault.</p>
            <p className="font-bold text-sm sm:text-base mt-2 mb-4 sm:mb-8">Only choose people who should be allowed to view your documents in emergency situations.</p>
            <div className="flex flex-col sm:flex-row sm:items-center">
              <p className="sm:mr-8 mb-3 sm:mb-0 text-lg sm:text-xl"><b className="text-xl sm:medium-text-xl">{ keyholders.length }</b> Keyholder{ keyholders.length !== 1 ? 's' : '' } </p>
              <Button
                variant="outlined"
                onClick={() => toggleModal("ADD_KEYHOLDER")}
                color="secondary">
                  <span className="flex items-center">
                    <FontAwesomeIcon icon="plus" className="mr-2 text-secondary"/>
                    <p>Add Keyholder</p>
                  </span>
              </Button>
            </div>
            <div className="mt-3 vault-form-content-container">
              {
                keyholders.length ?
                  _.map(keyholders, (user, index) =>
                    <div key={index} className={`vault-form-table-row  ${index % 2 ? 'alternate' : ''} sm:flex sm:items-center`}>
                      <p className="sm:flex-1 keyholder-table-field">{ user.name }</p>

                      <p className="sm:flex-1 keyholder-table-field my-2 sm:my-0">{ user.email }</p>
                      <div className="keyholder-actions-field flex items-center justify-end">
                        <FontAwesomeIcon icon="edit" className="mr-2 text-secondary cursor-pointer" onClick={() => editKeyholder(index)}/>
                        <FontAwesomeIcon icon="times" className="cursor-pointer" onClick={() => setKeyholders(removeElement(index, keyholders))}/>
                      </div>
                    </div>) :
                  <div/>

              }
            </div>
          </div>
        );
      case 2:
        // Guardians
        return (
          <div>
            <p className="text-sm sm:text-base mb-2">Guardians are trusted friends and family that act as a line of defense for your vault.</p>
            <p className="mb-4 sm:mb-8 text-sm sm:text-base font-bold">Guardians can never view the documents in your vault.</p>

            <div className="flex flex-col sm:flex-row sm:items-center">
              <p className="sm:mr-8 mb-3 sm:mb-0 text-lg sm:text-xl"><b className="text-xl sm:medium-text-xl">{ guardians.length }</b> Guardian{ guardians.length !== 1 ? 's' : '' } </p>
              <Button
                variant="outlined"
                onClick={() => toggleModal("ADD_GUARDIAN")}
                color="secondary">
                  <span className="flex items-center">
                    <FontAwesomeIcon icon="plus" className="mr-2 text-secondary"/>
                    <p>Add Guardian</p>
                  </span>
              </Button>
            </div>
            <div className="mt-3 vault-form-content-container">
              {
                guardians.length ?
                  _.map(guardians, (user, index) =>
                    <div key={index} className={`vault-form-table-row  ${index % 2 ? 'alternate' : ''} sm:flex sm:items-center`}>
                      <p className="sm:flex-1 keyholder-table-field">{ user.name }</p>
                      <p className="sm:flex-1 keyholder-table-field">{ user.email }</p>
                      <div className="keyholder-actions-field flex items-center justify-end">
                        <FontAwesomeIcon icon="edit" className="mr-2 text-secondary cursor-pointer" onClick={() => editGuardian(index)}/>
                        <FontAwesomeIcon icon="times" className="cursor-pointer" onClick={() => setGuardians(removeElement(index, guardians))}/>
                      </div>
                    </div>
                  ) :
                  <div/>
              }
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <p className="mb-2 text-sm sm:text-base">Choose the documents you want to store in your Vault.</p>
            <p className="mb-4 sm:mb-8 text-sm sm:text-base font-bold">They will only be available to Keyholders after they successfully request access to the Vault.</p>
            <div className="flex flex-col sm:flex-row sm:items-center">
              <p className="sm:mr-8 mb-3 sm:mb-0 text-lg sm:text-xl"><b className="text-xl sm:medium-text-xl">{ documents.length }</b> Document{ documents.length !== 1 ? 's' : '' } </p>
              <Button
                variant="outlined"
                onClick={() => toggleModal("FILE_UPLOADER")}
                color="secondary">
                  <span className="flex items-center">
                    <p className="text:sm sm:text-base">Upload Documents</p>
                  </span>
              </Button>
              {
                props.client && props.client.document_categories.length &&
                <div className="ml-4">
                  <Button
                    variant="outlined"
                    onClick={() => toggleModal("CLIENT_DOCUMENTS")}
                    color="primary">
                  <span className="flex items-center">
                    <p className="text:sm sm:text-base">Use Client Documents</p>
                  </span>
                  </Button>
                </div>
              }
            </div>
            <div className="mt-3 vault-form-content-container">
              {
                !!documents.length &&
                _.map(documents, (document, index) =>
                  <div key={index} className={`vault-form-table-row  ${index % 2 ? 'alternate' : ''} sm:flex sm:items-center`}>
                    <div className="sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <span className="text-sm sm:text-base sm:flex-1 documents-table-name-field">
                        { document.name }
                        <span className="text-sm text-grey ml-2 hidden sm:inline">({ humanFileSize(document.size) })</span>
                      </span>
                      <p className="text-xs text-grey mt-2 sm:hidden">{ humanFileSize(document.size) }</p>
                      <p className="text-grey mt-2 text-sm sm:mt-0 sm:text-base">
                        { moment(document.updated).format('L LT') }
                      </p>
                    </div>
                    <div className="document-actions-field flex items-center justify-end">
                      <Tooltip enterTouchDelay={50} title="Remove document.">
                        <div>
                          <FontAwesomeIcon icon="times" className="cursor-pointer" onClick={() => setDocuments(removeElement(index, documents))}/>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <p className="mb-10 sm:mb-8 text-sm sm:text-base">Review the options below to configure the security of your vault.</p>
            <div className="md:flex">
              <FormControl className="vault-options-field"
                           margin="dense"
                           variant="outlined">
                <InputLabel className="form-label-override form-long-label"
                            required
                            shrink={true}>
                  How many Guardians must approve each request?
                </InputLabel>
                <Select value={approvalLevel}
                        onChange={(event)=> setApprovalLevel(event.target.value)}>
                  <MenuItem value={"ALL"}>All Guardians.</MenuItem>
                  <MenuItem value={"MAJORITY"} disabled={guardians.length <= 1}>The majority of Guardians.</MenuItem>
                  <MenuItem value={"CUSTOM"} disabled={guardians.length <= 1}>A custom number of Guardians</MenuItem>
                </Select>
              </FormControl>
              <Tooltip enterTouchDelay={50} placement={"right"} title={"This controls how many Guardians must review and approve each access request. This should depend on the type of vault and nature of the information you store. Click for more details."}>
                <div className="flex items-center mt-1 md:ml-3 md:mt-0">
                  <FontAwesomeIcon icon={"question-circle"} className={"cursor-pointer text-warning"}/>
                  <p className="text-xs sm:text-sm text-grey ml-2">What is this?</p>
                </div>
              </Tooltip>
            </div>

            {
              approvalLevel === 'CUSTOM' &&
              <div className="mt-3">
                <Slider
                  defaultValue={numberApprovals}
                  className="vault-options-field"
                  aria-labelledby="discrete-slider"
                  valueLabelDisplay="auto"
                  step={1}
                  marks
                  min={1}
                  onChange={(event, value)=> setNumberApprovals(value)}
                  max={guardians.length ? guardians.length : 1}
                />
                <p>{numberApprovals} Approval{numberApprovals !== 1 ? "s" : ""} Required (Of {guardians.length})</p>
              </div>
            }
            <div className="mt-10 sm:mt-8 md:flex">
              <FormControl className="vault-options-field"
                           margin="dense"
                           variant="outlined">
                <InputLabel className="form-label-override form-long-label"
                            required
                            shrink={true}>
                  How long of a delay before Keyholders can access the vault?
                </InputLabel>
                <Select value={accessDelay}
                        onChange={(event)=> setAccessDelay(event.target.value)}>
                  {
                    _.map(Object.keys(accessDelayMapping), key => <MenuItem value={key}>{ _.startCase(accessDelayMapping[key]) }</MenuItem>)
                  }
                </Select>
              </FormControl>
              <Tooltip enterTouchDelay={50} placement={"right"} title={"The delay starts immediately after an access request is made. This gives you a chance to review and cancel a request if it isn't appropriate. Click for more details."}>
                <div className="flex items-center mt-1 md:ml-3 md:mt-0">
                  <FontAwesomeIcon icon={"question-circle"} className={"cursor-pointer text-warning"}/>
                  <p className="text-xs sm:text-sm text-grey ml-2">What is this?</p>
                </div>
              </Tooltip>
            </div>
            {
              accessDelay === 'NONE' && !guardians.length  &&
                <div className="flex items-center mt-5 warning-container">
                  <FontAwesomeIcon icon="exclamation-circle" size="2x" className="text-alert"/>
                  <div className="flex-1 left-spacer text-sm sm:text-base">
                    <p>Since you haven't assigned any Guardians, <span className="font-bold">Keyholders are able to immediately access your
                      Vault on request.</span></p>
                    <p className="mt-3">Fidelius recommends adding an access delay to your Vault so you have a chance to review requests before Keyholders can access the documents.</p>
                  </div>

                </div>
            }
          </div>
        )
    }
  };

  return (
    <div className="mt-6 sm:mt-0">
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel className="text-left">
              {
                index <= allowedStep && !checkStepDisabled(0) ?
                  <a onClick={() => setActiveStep(index)}>
                    <span className="text-black">{label}</span>
                  </a>
                  :
                  <p>{ label }</p>
              }
            </StepLabel>
            <StepContent>
              {getStepContent(index)}
              <div className="flex end-xs top-spacer-large">
                {
                  !!index &&
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                  >
                    Previous
                  </Button>
                }
                {
                  !(activeStep === steps.length - 1) &&
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={nextStep}
                    disabled={checkStepDisabled(index)}
                  >
                    Next
                  </Button>
                }

              </div>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      <Divider/>
      <div className="flex justify-end mt-3">
        <div className="right-spacer">
          <Button
            variant="outlined"
            onClick={() => navigate(`/vaults/${props.editMode ? props.vault.id : ''}`)}
          >Cancel</Button>
        </div>
        <div>
          <Button
            variant="contained"
            color="primary"
            disabled={allowedStep < 4 || checkStepDisabled(0)}
            onClick={saveVault}
          >{props.editMode ? 'Save Changes' : 'Create My Vault'}</Button>
        </div>
      </div>

      {
        openFileUploadModal &&
        <DashboardModal
          uppy={uppy}
          open={openFileUploadModal}
          target={document.body}
          onRequestClose={() => toggleModal("FILE_UPLOADER")}
          proudlyDisplayPoweredByUppy={false}
          metaFields={[
            { id: 'name', name: 'Name', placeholder: 'File name' }
          ]}
        />
      }

      <PersonInfoModal open={openNewKeyholderModal}
                       title={"Add Keyholder"}
                       save={updateKeyholders}
                       userType={KEYHOLDER}
                       ownerEmail={_.get(props.user, 'email', '')}
                       keyholders={_.map(keyholders, "email")}
                       guardians={_.map(guardians, "email")}
                       toggleModal={() => toggleModal("ADD_KEYHOLDER")}/>

      <PersonInfoModal open={openEditKeyholderModal}
                       title={"Edit Keyholder"}
                       name={editedKeyholder !== null ? keyholders[editedKeyholder].name : ""}
                       email={editedKeyholder !== null ? keyholders[editedKeyholder].email : ""}
                       save={updateKeyholders}
                       userType={KEYHOLDER}
                       ownerEmail={_.get(props.user, 'email', '')}
                       keyholders={_.map(keyholders, "email")}
                       guardians={_.map(guardians, "email")}
                       toggleModal={() => toggleModal("EDIT_KEYHOLDER")}/>

      <PersonInfoModal open={openNewGuardianModal}
                       title={"Add Guardian"}
                       save={updateGuardians}
                       userType={GUARDIAN}
                       ownerEmail={_.get(props.user, 'email', '')}
                       keyholders={_.map(keyholders, "email")}
                       guardians={_.map(guardians, "email")}
                       toggleModal={() => toggleModal("ADD_GUARDIAN")}/>

      <PersonInfoModal open={openEditGuardianModal}
                       title={"Edit Guardian"}
                       name={editedGuardian !== null ? guardians[editedGuardian].name : ""}
                       email={editedGuardian !== null ? guardians[editedGuardian].email : ""}
                       save={updateGuardians}
                       userType={GUARDIAN}
                       ownerEmail={_.get(props.user, 'email', '')}
                       keyholders={_.map(keyholders, "email")}
                       guardians={_.map(guardians, "email")}
                       toggleModal={() => toggleModal("EDIT_GUARDIAN")}/>

      {
        props.client &&
        <ClientDocumentsModal open={openClientDocumentsModal}
                              documentCategories={props.client.document_categories}
                              documents={documents}
                              updateDocuments={updateDocuments}
                              toggleModal={() => toggleModal("CLIENT_DOCUMENTS")}/>
      }

    </div>
  );
}

const mapStateToProps = (state, props) => {
  return {
    ...props,
    user: state.user.user,
  };
};

export default compose(
  connect(
    mapStateToProps,
    { openConfirmationModal }
  )
)(VaultForm);
